/**
 * 通用的场景匹配器，传入场景列表以及对应的场景值，自动匹配场景。
 * 会根据传入的列表的顺序，返回场景因子的场景。
 * 若存在多个满足场景因子的场景，则返回匹配数量最多的场景。
 *
 * @param sceneArray 场景列表
 * @param factors 场景因子，为key value对象
 * @returns 返回sceneArray中匹配的scene，若无匹配则返回为空
 */
export function match<P, T extends Factors>(sceneArray: Array<Scene<P, T>>, factors: T) {
  const [sceneConfig] = sceneArray.filter((scene) => {
    const { factorList: sceneFactors } = scene;
    // 遍历场景的因子，若传入的因子与场景的因子值不匹配，则返回false
    for (const [key, value] of Object.entries(sceneFactors)) {
      const isEqual = value === factors[key]; // 值相等
      const isContain = Array.isArray(value) && value.includes(factors[key]); // 如果为数组，则判断是否包含
      const isFuncReturnOk = typeof value === 'function' && value(factors[key]); // 如果为函数，则判断函数是否返回true
      if (!(isEqual || isContain || isFuncReturnOk)) {
        return false;
      }
    }
    return true;
  })
    // 若有匹配多个场景的情况，则返回场景因子数量多的场景
    .sort((a, b) => Object.keys(b.factorList).length - Object.keys(a.factorList).length);
  return sceneConfig?.value;
};

/** 转化为原类型或其数组类型 */
type Listify<T> = T | T[];

/**
 * 场景对象，包括场景匹配的因子及对应返回的场景值
 */
export type Scene<P, T extends Factors> = {
  /** 场景相关的匹配因子 */
  factorList: {
    [key in keyof T]?: Listify<Exclude<T[key], undefined>> | ((factors: T[key]) => boolean);
  },
  /** 场景值 */
  value: P,
};

/** 场景因子对象，为key value对象 */
export type Factors = Record<string, any>;
